import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import CreateNewMetadataDialog from '@/shared/components/dialogs/CreateNewMetadataDialog';
import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import MetadataFormAccordion from '@/shared/components/forms/MetadataField/components/MetadataFormAccordion';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import WaveUIPageLoader from '@/shared/components/utilities/WaveUIPageLoader';
import WaveUIAccordion from '@/shared/components/widgets/WaveUIAccordion';
import { AppPath } from '@/shared/constants/path';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import DialogName from '@/shared/types/dialogName.enum';

import ShippingProvidersSelect from '../components/FulfilmentProvidersSelect/FulfilmentProvidersSelect';
import useFulfilmentProvidersSelect from '../components/FulfilmentProvidersSelect/hooks/useFulfilmentProvidersSelect';
import useCreateMetadata from '../hooks/useCreateMetadata';
import useFetchInventoryMetadata from '../hooks/useFetchInventoryMetadata';
import InventoryCreateErrorDialog from './components/InventoryCreateErrorDialog';
import Title from './components/Title';
import useCreateInventory from './hooks/useCreateInventory';

const InventoryCreate: FC = () => {
  const { t } = useTranslation(['inventory', 'common']);

  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const { refetchMetadata, onCreateNewMetadata, setRefetchMetadata } =
    useCreateMetadata({
      onUpdateDialogState,
      onUpdateProgressDialog,
      onUpdateNotificationBar,
    });

  const { metadata } = useFetchInventoryMetadata({
    refetchMetadata,
    setRefetchMetadata,
  });

  const {
    shouldPersistForm,
    keyToResetForm,
    createdInventoryItem,
    setShouldPersistForm,
    onCreateInventory,
  } = useCreateInventory({
    onUpdateNotificationBar,
    onUpdateProgressDialog,
    onUpdateDialogState,
  });

  const fulfilmentProvidersSelectState = useFulfilmentProvidersSelect();

  const [inventory, setInventory] = useState<{
    required: Record<string, unknown>;
    optional: Record<string, unknown>;
  }>({
    required: {},
    optional: {},
  });

  const [inventoryDetails, setInventoryDetails] = useState<{
    required: Record<string, unknown>;
    optional: Record<string, unknown>;
    initialData?: Record<string, unknown>;
    qty: string;
  }>({
    required: {},
    optional: {},
    qty: '0',
    initialData: {
      is_fulfilable: true,
    },
  });

  return (
    <>
      <Title
        shouldPersistForm={shouldPersistForm}
        setShouldPersistForm={setShouldPersistForm}
        handleMetadataCreate={() => {
          onUpdateDialogState(DialogName.CreateMetadata, {
            open: true,
            errors: [],
          });
        }}
        handleSaveChanges={() => {
          const { selectedFulfilmentProvider } = fulfilmentProvidersSelectState;

          void onCreateInventory({
            ...inventory.required,
            ...inventory.optional,
            fulfilment_provider_id: selectedFulfilmentProvider,
            qty: parseInt(inventoryDetails.qty),
            ext_inventory_id: '',
            atomic_metadata: {
              ...inventoryDetails.initialData,
              ...inventoryDetails.required,
              ...inventoryDetails.optional,
            },
          });
        }}
      />

      <Box
        key={keyToResetForm}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        {!metadata && <WaveUIPageLoader />}

        {metadata && (
          <>
            <MetadataFormAccordion
              accordionProps={{ title: 'Inventory' }}
              metadata={metadata}
              levelConstraint='INVENTORY'
              fields={inventory}
              getValues={(values) => {
                setInventory((prev) => ({
                  ...prev,
                  required: { ...prev.required, ...values.required },
                  optional: { ...prev.optional, ...values.optional },
                }));
              }}
            />

            <WaveUIAccordion
              title={t('inventory:inventoryItemsDetails')}
              defaultExpanded
            >
              <TextField
                placeholder='Set items quantity ...'
                required
                label='Inventory quantity'
                variant='outlined'
                value={inventoryDetails.qty}
                inputProps={{ type: 'number' }}
                onChange={(e) =>
                  setInventoryDetails((prev) => ({
                    ...prev,
                    qty: e.target.value,
                  }))
                }
              />
              <ShippingProvidersSelect
                fulfilmentProvidersSelectState={fulfilmentProvidersSelectState}
              />

              <MetadataFormAccordion
                accordionProps={{
                  title: null,
                  elevation: 0,
                  summaryProps: { hidden: true },
                  sx: { mx: -2 },
                }}
                metadata={metadata}
                initialData={inventoryDetails.initialData}
                levelConstraint='ATOM'
                fields={inventoryDetails}
                getValues={(values) => {
                  setInventoryDetails((prev) => ({
                    ...prev,
                    required: { ...prev.required, ...values.required },
                    optional: { ...prev.optional, ...values.optional },
                  }));
                }}
              />
            </WaveUIAccordion>
          </>
        )}
      </Box>

      {dialog?.createMetadata?.open && (
        <CreateNewMetadataDialog
          metadata={metadata}
          module='inventory'
          errors={dialog.createMetadata.errors}
          onClose={() =>
            onUpdateDialogState(DialogName.CreateMetadata, {
              open: false,
            })
          }
          proceed={(formToSave) => {
            void onCreateNewMetadata(formToSave);
          }}
        />
      )}

      {dialog?.error?.open && (
        <InventoryCreateErrorDialog
          errors={dialog?.error?.errors}
          onClose={() => {
            onUpdateDialogState(DialogName.Error, {
              open: false,
            });
          }}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
        renderMessage={(message) => {
          if (createdInventoryItem) {
            return (
              <>
                {message}
                <Link
                  to={`${AppPath.Inventory}/${createdInventoryItem.inventory_id}`}
                >
                  <Button variant='contained' size='small' sx={{ ml: 2 }}>
                    {t('inventory:viewInventory')}
                  </Button>
                </Link>
              </>
            );
          }

          return message;
        }}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
      />
    </>
  );
};

export default InventoryCreate;
